import { createTheme } from '@mui/material/styles'
import { ptBR } from '@mui/material/locale'

/**
 * ADR-0024 D8 - tokens de design centralizados.
 *
 * REGRA: nenhuma cor, espacamento, raio ou tipografia e escrito diretamente em
 * componente. Tudo vem daqui, para que a identidade visual do produto seja
 * trocavel em um unico ponto.
 *
 * FE-1 B0.b (2026-07-27) - identidade visual "Viga Ascendente" aplicada.
 * Fonte normativa: Manual de Marca v4.0, 27/07/2026, secoes 8 (paleta),
 * 9 (tipografia) e 11 (acessibilidade).
 *
 * ------------------------------------------------------------------
 * RESTRICAO DE ACESSIBILIDADE - LEIA ANTES DE MUDAR QUALQUER COR
 * ------------------------------------------------------------------
 * O manual e explicito: "O logo e isento de WCAG; a interface do produto
 * nao e." Tres cores da marca REPROVAM como cor de texto sobre branco:
 *
 *   laranja    #F97316  ->  2,80:1   (so grafico, ou texto >= 24px bold)
 *   azul claro #3BA7EA  ->  2,66:1   (so grafico)
 *   cinza      #94A3B8  ->  2,56:1   (so borda e icone decorativo)
 *
 * E a rampa escura do azul tambem NAO basta: #2A8FD4 da 3,51:1 sobre
 * branco - reprova AA para texto normal e para `contrastText` branco.
 *
 * O minimo AA para texto normal e 4,5:1. Por isso:
 *
 * 1. `primary` e o AZUL ESCURO (15,39:1 - AAA), nunca o laranja.
 * 2. O laranja entra como `secondary`, ou seja ACENTO, e seu
 *    `contrastText` e FIXADO em azul escuro (5,49:1 - AA). Deixar o MUI
 *    escolher produziria branco sobre laranja = 2,80:1, que REPROVA.
 * 3. `info` (links, chips, destaques) NAO pode ser #3BA7EA nem #2A8FD4.
 *    Usa-se #1374B0, variacao de luminosidade do MESMO matiz: 5,06:1
 *    sobre branco, 4,54:1 sobre `background.default`, e 5,06:1 para
 *    branco sobre ele (botao contained). Um unico valor resolve os tres
 *    usos porque a razao de contraste e simetrica.
 * 4. `text.secondary` NAO pode ser o cinza #94A3B8 do manual. Usa-se
 *    #5D708C, que e uma variacao de luminosidade do MESMO matiz - a
 *    mesma logica das "rampas" declaradas na secao 8 - calibrada para
 *    5,05:1 sobre branco e 4,53:1 sobre `background.default`.
 *
 * Estes limites estao travados em `theme.test.ts`. Alterar uma cor de
 * texto sem verificar contraste quebra a suite de proposito.
 * ------------------------------------------------------------------
 */

/**
 * Paleta oficial da marca (Manual v4.0, secao 8) - valores literais.
 * "Nenhuma cor nova. As rampas sao variacoes de luminosidade dos mesmos
 * cinco matizes."
 */
export const brand = {
  /** Haste esquerda (a base). Texto, fundos densos. 15,39:1 sobre branco. */
  navy: '#0B2545',
  navyLight: '#1C4577',
  navyDark: '#09203D',

  /** A viga e o "H" do logotipo. ACENTO UNICO - nunca texto corrido. */
  orange: '#F97316',
  orangeLight: '#FFA452',
  orangeDark: '#E5590A',

  /** Haste direita (a ascensao). Graficos. NAO usar como texto sobre branco. */
  blue: '#3BA7EA',
  blueLight: '#6FC7FF',
  blueDark: '#2A8FD4',

  /** A palavra "ONE", bordas. NAO usar como texto sobre branco (2,56:1). */
  grey: '#94A3B8',
  greyRampLight: '#A9B7C8',
  greyRampDark: '#7F8FA3',

  /** Fundos de secao, divisorias, superficies. */
  greyLight: '#E2E8F0',

  white: '#FFFFFF',
} as const

/**
 * Tokens derivados - NAO fazem parte da paleta publicada, existem apenas
 * porque a interface tem exigencia de contraste que o manual nao cobre.
 * Cada um e variacao de luminosidade de um matiz oficial.
 */
export const derived = {
  /** Variacao escura de brand.grey. 5,05:1 sobre branco / 4,53:1 sobre surfaceAlt. */
  greyText: '#5D708C',
  /** Variacao escura de brand.blue. 5,06:1 sobre branco / 4,54:1 sobre surfaceAlt. */
  blueText: '#1374B0',
  /** Passo mais escuro de derived.blueText, para estados hover/active. 8,10:1. */
  blueTextDark: '#0E547F',
  /** Ponto medio de luminosidade entre brand.greyLight e branco. */
  surfaceAlt: '#F0F3F7',
} as const

/**
 * S31-7 (2026-09-24) - DEGRADES da marca para as superficies graficas do layout
 * hibrido (esqueleto Rippling, tabelas Stripe, IA estilo Attio). Construidos SO
 * com as rampas da secao 8 do manual: nenhuma cor nova.
 * REGRA: degrade e FUNDO de painel/area grafica. Texto sobre `brandPanel` usa
 * branco (15,4:1) ou brand.grey (6,0:1); o `accent` laranja nunca leva texto.
 */
export const gradients = {
  /** Painel de marca: navy (rampa escura -> clara) com brilho do azul claro no canto. */
  brandPanel: `radial-gradient(120% 80% at 100% 0%, ${brand.blueDark}40 0%, transparent 60%), linear-gradient(160deg, ${brand.navyDark} 0%, ${brand.navy} 55%, ${brand.navyLight} 100%)`,
  /** A "viga": acento laranja na rampa oficial. So grafico. */
  accent: `linear-gradient(90deg, ${brand.orangeLight} 0%, ${brand.orange} 50%, ${brand.orangeDark} 100%)`,
} as const

export const tokens = {
  color: {
    primary: brand.navy,
    /** ACENTO. contrastText fixado em navy - ver restricao no topo. */
    secondary: brand.orange,
    /** Link e destaque grafico. brand.blue e brand.blueDark REPROVAM AA. */
    info: derived.blueText,
    error: '#B3261E',
    warning: '#8A6100',
    success: '#1B6B3A',
    surface: brand.white,
    background: derived.surfaceAlt,
    divider: brand.greyLight,
    textPrimary: '#1A1C1E',
    textSecondary: derived.greyText,
  },
  radius: {
    sm: 4,
    md: 8,
    lg: 12,
  },
  spacingUnit: 8,
} as const

/**
 * Manual v4.0 secao 9 - tipografia.
 * Montserrat 700 nos titulos do produto; Inter 400/600 no texto corrido e
 * na UI ("melhor legibilidade em tabelas densas").
 *
 * As duas sao AUTO-HOSPEDADAS via @fontsource (importadas em main.tsx).
 * NAO usar Google Fonts: a requisicao ao CDN vaza o IP do usuario para um
 * terceiro a cada carregamento de pagina, o que conflita com a politica de
 * minimizacao de dado pessoal do ADR-0014.
 */
const FONT_HEADING = '"Montserrat", "Segoe UI", Roboto, Arial, sans-serif'
const FONT_BODY = '"Inter", system-ui, -apple-system, "Segoe UI", Roboto, Arial, sans-serif'

export const fonts = {
  heading: FONT_HEADING,
  body: FONT_BODY,
} as const

export const theme = createTheme(
  {
    palette: {
      mode: 'light',
      primary: {
        main: tokens.color.primary,
        light: brand.navyLight,
        dark: brand.navyDark,
        contrastText: brand.white,
      },
      secondary: {
        main: tokens.color.secondary,
        light: brand.orangeLight,
        dark: brand.orangeDark,
        // FIXADO. Sem isto o MUI derivaria branco (2,80:1 - REPROVA AA).
        contrastText: brand.navy,
      },
      info: {
        main: tokens.color.info,
        // brand.blue so e legivel sobre o azul escuro (5,79:1 - Manual sec. 11).
        light: brand.blue,
        dark: derived.blueTextDark,
        contrastText: brand.white,
      },
      error: { main: tokens.color.error, contrastText: brand.white },
      warning: { main: tokens.color.warning, contrastText: brand.white },
      success: { main: tokens.color.success, contrastText: brand.white },
      divider: tokens.color.divider,
      background: {
        default: tokens.color.background,
        paper: tokens.color.surface,
      },
      text: {
        primary: tokens.color.textPrimary,
        secondary: tokens.color.textSecondary,
      },
    },
    shape: { borderRadius: tokens.radius.md },
    spacing: tokens.spacingUnit,
    typography: {
      fontFamily: FONT_BODY,
      h1: { fontFamily: FONT_HEADING, fontSize: '2rem', fontWeight: 700 },
      h2: { fontFamily: FONT_HEADING, fontSize: '1.5rem', fontWeight: 700 },
      h3: { fontFamily: FONT_HEADING, fontSize: '1.25rem', fontWeight: 700 },
      h4: { fontFamily: FONT_HEADING, fontSize: '1.125rem', fontWeight: 700 },
      h5: { fontFamily: FONT_HEADING, fontSize: '1rem', fontWeight: 700 },
      h6: { fontFamily: FONT_HEADING, fontSize: '0.9375rem', fontWeight: 700 },
      button: { fontFamily: FONT_BODY, textTransform: 'none', fontWeight: 600 },
    },
    components: {
      // Manual secao 7: "Barra superior do SaaS - aqui a regra manda Chapada."
      // A barra usa o azul escuro; o logo chapado entra como asset em /brand.
      MuiAppBar: {
        defaultProps: { elevation: 0, color: 'primary' },
        styleOverrides: {
          root: { borderBottom: `1px solid ${brand.navyLight}` },
        },
      },
    },
  },
  ptBR,
)
